import { Inject, Injectable } from '@angular/core';
import {
    AppNotificationService,
    AppVersionAndSessionService,
    AuthenticationTimeoutService,
    BaseInitializationService,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigRequestsInstanceToken,
    ConfigRequestsInterface,
    ConfigService,
    ConnectivityService,
    EncryptionInitializer,
    EncryptionMigrator,
    EventService,
    FileStorageInjectionToken,
    FileStorageServiceInterface,
    INIT_MESSAGE_KEY,
    INIT_MESSAGE_STATE,
    InitError,
    InitMessage,
    LocalStorageAppStateService,
    LocalStorageCommonService,
    MaterialService,
    SessionService,
    SideMenuInstanceToken,
    SideMenuInterface,
    UriUtilitiesService,
    UserGidService,
    UserMasterService,
    ValueHelperService,
    WeeklyWorkTimeService,
} from '@evorait/pace-core';
import { FormsService } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { OfflineService } from '@evorait/pace-offline';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, tap } from 'rxjs/operators';
import { SynchronizationService } from '../synchronization/synchronization.service';
import { TestFormService } from '../test-forms-service/test-form.service';

@Injectable({
    providedIn: 'root',
})
export class InitializationService extends BaseInitializationService {
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) protected readonly clientManager: ClientManagerInterface,
        @Inject(SideMenuInstanceToken) protected readonly sideMenuService: SideMenuInterface,
        @Inject(ConfigRequestsInstanceToken) protected readonly configInterfaceService: ConfigRequestsInterface,
        protected readonly modalService: BsModalService,
        protected readonly localStorageAppStateService: LocalStorageAppStateService,
        protected readonly connectivityService: ConnectivityService,
        protected readonly uriUtilities: UriUtilitiesService,
        protected readonly userMasterService: UserMasterService,
        protected readonly appNotificationService: AppNotificationService,
        protected readonly translate: TranslateService,
        protected readonly eventService: EventService,
        protected readonly synchronizationService: SynchronizationService,
        protected readonly configService: ConfigService,
        protected readonly weeklyWorkTimeService: WeeklyWorkTimeService,
        protected readonly offlineService: OfflineService,
        protected readonly userGidService: UserGidService,
        protected readonly authenticationTimeoutService: AuthenticationTimeoutService,
        protected readonly appVersionService: AppVersionAndSessionService,
        private readonly valueHelperService: ValueHelperService,
        protected readonly encryptionInitializer: EncryptionInitializer,
        protected readonly localStorageCommonService: LocalStorageCommonService,
        protected readonly sessionService: SessionService,
        protected readonly formsService: FormsService,
        protected readonly testFormService: TestFormService,
        @Inject(FileStorageInjectionToken) protected readonly fileStorageServiceInterface: FileStorageServiceInterface,
        protected readonly materialService: MaterialService,
        protected readonly encryptionMigrator: EncryptionMigrator,
    ) {
        super(
            log,
            clientManager,
            sideMenuService,
            configInterfaceService,
            modalService,
            localStorageAppStateService,
            connectivityService,
            uriUtilities,
            userMasterService,
            appNotificationService,
            translate,
            eventService,
            synchronizationService,
            configService,
            weeklyWorkTimeService,
            offlineService,
            userGidService,
            authenticationTimeoutService,
            appVersionService,
            localStorageCommonService,
            sessionService,
            fileStorageServiceInterface,
            materialService,
            encryptionMigrator,
            encryptionInitializer,
        );
    }

    protected setupInitProcessPipe(): void {
        // initialize empty observable as starting point
        this.init$ = of(undefined);
        this.init$ = this.attachEncryptionServiceInit(this.init$); // INIT_MESSAGE_KEY.ENCRYPTION_KEY
        this.init$ = this.upgradeLegacyEncryption(this.init$);
        this.init$ = this.attachUserMasterServiceInit(this.init$); // INIT_MESSAGE_KEY.USER_MASTER_DATA
        this.init$ = this.attachTimeoutAuthenticationTimer(this.init$);
    }

    public setupInitMessages(): void {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                INIT_MESSAGE_STATE.ADDED,
                of(`Added ${INIT_MESSAGE_KEY.ENCRYPTION_KEY} to init pipe`),
                of('0s'),
            ),
        );
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.USER_MASTER_DATA,
                INIT_MESSAGE_STATE.ADDED,
                of(`Added ${INIT_MESSAGE_KEY.USER_MASTER_DATA} to init pipe`),
                of('0s'),
            ),
        );
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.SERVICE_INIT,
                INIT_MESSAGE_STATE.ADDED,
                of(`Added ${INIT_MESSAGE_KEY.SERVICE_INIT} to init pipe`),
                of('0s'),
            ),
        );
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.SIDE_MENU,
                INIT_MESSAGE_STATE.ADDED,
                of(`Added ${INIT_MESSAGE_KEY.SIDE_MENU} to init pipe`),
                of('0s'),
            ),
        );
    }

    public attachServiceInit(obs$: Observable<unknown>): Observable<void> {
        let startTimer: moment.Moment;
        return obs$.pipe(
            tap({
                next: () => (startTimer = moment()),
                error: () => (startTimer = moment()),
            }),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SERVICE_INIT,
                        INIT_MESSAGE_STATE.PENDING,
                        of(`Initializing odata consuming services`),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SERVICE_INIT,
                        INIT_MESSAGE_STATE.PENDING,
                        of(`Initializing ConfigService, ValueHelperService, TestFormService`),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return forkJoin([this.configService.init(), this.valueHelperService.init()]);
            }),
            // we need config service to be initialized before calling testFormService. Therefore calling addTestFormsListToState in this method.
            mergeMap(() => {
                return this.testFormService.addTestFormsListToState().pipe(
                    catchError((error) => {
                        this.log.warn(
                            `InitializationService: attachServiceInit: Error while initializing services: ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        return of(undefined);
                    }),
                );
            }),
            catchError((error) => {
                this.log.warn(
                    `InitializationService: attachServiceInit: Error while initializing services: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return of(undefined);
            }),
            tap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SERVICE_INIT,
                        INIT_MESSAGE_STATE.SUCCESS,
                        of(`Initializing ConfigService, ValueHelperService, TestFormService`),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
            }),
            map(() => undefined),
        );
    }

    // Function to load any effects that should happen after initialization is completed.
    public executeRemainingSideEffects(): void {
        this.formsService.restoreLastOpenedForm();
    }

    private attachEncryptionServiceInit(obs$: Observable<unknown>): Observable<void> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                INIT_MESSAGE_STATE.ADDED,
                of(`Added ${INIT_MESSAGE_KEY.ENCRYPTION_KEY} to init pipe`),
                of('0s'),
            ),
        );
        let startTimer: moment.Moment;
        return obs$.pipe(
            tap({
                next: () => (startTimer = moment()),
                error: () => (startTimer = moment()),
            }),
            tap(() =>
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_ENCRYPTION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                ),
            ),
            mergeMap(() => this.encryptionInitializer.init()),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_ENCRYPTION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return of(undefined);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_ENCRYPTION_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
            finalize(() =>
                this.log.debug(
                    `InitializationService: attachEncryptionServiceInit: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                ),
            ),
        );
    }
}
